<template>
    <CompassModal
        class="link-object-dialog"
        :visible="isVisible"
        :title="
            showSearch
                ? $t.__('common.object.linkObjectDialog.add_related_title')
                : $t.__('common.object.linkObjectDialog.update_related_title')
        "
        :primaryAction="primaryAction"
        :secondaryActions="secondaryActions"
        @update:visible="handleClose"
    >
        <template #default>
            <CompassAlert
                v-if="hasError"
                type="error"
                :description="$t.__('common.object.linkObjectDialog.error_message')"
                :showClose="false"
            />
            <SearchObjectToLinkComponent
                v-show="showSearch"
                :isVisible="showSearch"
                :emptyStateData="emptyStateData"
                @objectSelected="handleSelectObject"
            />
            <div v-if="!showSearch" class="link-object-dialog__content">
                <div class="typographies-paragraph-medium-bold">
                    {{
                        $t.n__(
                            'common.object.linkObjectDialog.related_listing_title',
                            null,
                            objectsSelected.length,
                            objectsSelected.length,
                        )
                    }}
                </div>

                <div class="link-object-dialog__object-container">
                    <template v-if="objectsSelected.length > 0">
                        <template v-for="objectSelected in objectsSelected">
                            <BlockCompany
                                v-if="isCompany(objectSelected)"
                                :key="`company-${objectSelected.data.type}-${objectSelected.data.id}`"
                                :data="objectSelected.data"
                                :showMobile="false"
                                :showAddress="false"
                                variant="linked"
                                @unlink="unlinkObject(objectSelected)"
                            />
                            <BlockContact
                                v-else-if="isContact(objectSelected)"
                                :key="`contact-${objectSelected.data.id}`"
                                :data="objectSelected.data"
                                :showPhone="false"
                                variant="linked"
                                @unlink="unlinkObject(objectSelected)"
                            />
                            <BlockDocument
                                v-else-if="isDocument(objectSelected)"
                                :key="`document-${objectSelected.data.type}-${objectSelected.data.id}`"
                                :data="objectSelected.data"
                                variant="linked"
                                @unlink="unlinkObject(objectSelected)"
                            />
                            <BlockOpportunity
                                v-else-if="isOpportunity(objectSelected)"
                                :key="`opportunity-${objectSelected.data.id}`"
                                :data="objectSelected.data"
                                variant="linked"
                                @unlink="unlinkObject(objectSelected)"
                            />
                            <BlockIndividual
                                v-else-if="isIndividual(objectSelected)"
                                :key="`individual-${objectSelected.data.type}-${objectSelected.data.id}`"
                                :data="objectSelected.data"
                                variant="linked"
                                @unlink="unlinkObject(objectSelected)"
                            />
                        </template>
                    </template>

                    <div v-else class="object-block--empty typographies-heading-h4">
                        {{ $t.__('common.object.linkObjectDialog.no_related_objects') }}
                    </div>
                </div>

                <CompassPlainButton
                    :label="$t.__('common.object.linkObjectDialog.actions.add_new_related')"
                    @click="showSearch = true"
                />
            </div>
        </template>
    </CompassModal>
</template>
<script>
import SearchObjectToLinkComponent from 'vcomponents/common/object/SearchObjectToLinkComponent.vue';
import BlockCompany from 'vcomponents/common/object/BlockCompany.vue';
import BlockIndividual from 'vcomponents/common/object/BlockIndividual.vue';
import BlockContact from 'vcomponents/common/object/BlockContact.vue';
import BlockDocument from 'vcomponents/common/object/BlockDocument.vue';
import BlockOpportunity from 'vcomponents/common/object/BlockOpportunity.vue';

export default {
    components: {
        SearchObjectToLinkComponent,
        BlockCompany,
        BlockContact,
        BlockDocument,
        BlockOpportunity,
        BlockIndividual,
    },
    props: {
        isVisible: {
            type: Boolean,
            default: false,
        },
        isLoading: {
            type: Boolean,
            default: false,
        },
        related: {
            type: Array,
            default: () => {
                return [];
            },
        },
        hasError: {
            type: Boolean,
            default: false,
        },
        emptyStateData: {
            type: Object,
            default: () => {
                return {
                    title: '',
                    caption: '',
                    illustrationPath: '',
                };
            },
        },
    },
    data() {
        return {
            objectsSelected: [],
            showSearch: true,
            secondaryActions: [
                {
                    label: this.$t.__('common.actions.cancel'),
                    action: () => {
                        this.handleClose();
                    },
                },
            ],
        };
    },
    computed: {
        primaryAction() {
            return {
                label: this.$t.__('common.actions.save'),
                action: () => this.confirmLinkObject(),
                loading: this.isLoading,
                disabled:
                    this.showSearch ||
                    (this.objectsSelected.length === 0 && this.related.length === 0),
            };
        },
    },
    watch: {
        related(newRelated) {
            this.objectsSelected = [...newRelated];
            this.showSearch = this.objectsSelected.length === 0;
        },
    },
    created() {
        this.objectsSelected = [...this.related];
        this.showSearch = this.objectsSelected.length === 0;
    },
    methods: {
        confirmLinkObject() {
            this.$emit('update:related', this.objectsSelected);
        },
        handleSelectObject(object) {
            if (
                !this.objectsSelected.some((selected) => {
                    if (selected.data.object) {
                        return (
                            selected.data.object.id === object.data.object.id &&
                            selected.data.object.sub_type === object.data.object.sub_type
                        );
                    }
                    return (
                        selected.data.id === object.data.object.id &&
                        selected.data.type === object.data.object.sub_type
                    );
                })
            ) {
                this.objectsSelected.push(object);
            }

            this.showSearch = false;
        },
        unlinkObject(objectToUnlink) {
            this.objectsSelected = this.objectsSelected.filter((objectSelected) => {
                const objectSelectedId = objectSelected.data.object
                    ? objectSelected.data.object.id
                    : objectSelected.data.id;
                const objectToUnlinkId = objectToUnlink.data.object
                    ? objectToUnlink.data.object.id
                    : objectToUnlink.data.id;
                const objectSelectedType = objectSelected.data.object
                    ? objectSelected.data.object.sub_type
                    : objectSelected.data.type;
                const objectToUnlinkType = objectToUnlink.data.object
                    ? objectToUnlink.data.object.sub_type
                    : objectToUnlink.data.type;
                return !(
                    objectSelectedId === objectToUnlinkId &&
                    objectSelectedType === objectToUnlinkType
                );
            });
        },
        isCompany(object) {
            if (!object.data.is_individual) {
                switch (object.blockType) {
                    case 'company':
                    case 'prospect':
                    case 'supplier':
                    case 'third':
                    case 'client':
                        return true;
                    default:
                        return false;
                }
            }
            return false;
        },
        isContact(object) {
            switch (object.blockType) {
                case 'contact':
                case 'people':
                    return true;
                default:
                    return false;
            }
        },
        isOpportunity(object) {
            return object.blockType === 'opportunity';
        },
        isIndividual(object) {
            return object.blockType === 'individual' || object.data.is_individual;
        },
        isDocument(object) {
            return (
                !this.isCompany(object) &&
                !this.isContact(object) &&
                !this.isOpportunity(object) &&
                !this.isIndividual(object)
            );
        },
        handleClose() {
            this.objectsSelected = [...this.related];
            this.showSearch = this.objectsSelected.length === 0;
            this.$emit('close');
        },
    },
};
</script>

<style lang="scss" scoped>
.link-object-dialog {
    .object-block {
        width: 100%;

        &--empty {
            color: var(--colors-common-text-standard-disabled);
            display: flex;
            align-items: center;
            justify-content: center;
            padding: var(--spacing-4) var(--spacing-3);
            border: 1px solid var(--colors-common-border-primary-subtle-default);
            border-radius: var(--spacing-0);
            background-color: var(--colors-common-background-primary-subtle);
        }
    }

    &__content {
        display: flex;
        flex-direction: column;
        align-items: flex-start;
        gap: var(--spacing-2);
    }

    &__object-container {
        width: 100%;
        display: flex;
        flex-direction: column;
        gap: var(--spacing-1);
    }
}
</style>
